export type UserStatus = 'INVITED' | 'ACTIVE' | 'DISABLED';
export type Audience = 'INTERNAL' | 'CUSTOMER';

export interface Me {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    emailVerifiedAt: string | null;
    roles: { id: string; name: string; audience: Audience }[];
  };
  tenant: {
    id: string;
    slug: string;
    name: string;
    isPlatform: boolean;
    primaryColor: string;
    accentColor: string | null;
    hasLogo: boolean;
    brandingVersion: string;
  };
  customer: { id: string; name: string } | null;
  permissions: string[];
  csrfToken: string;
}

export interface PublicBranding {
  slug: string;
  name: string;
  primaryColor: string;
  accentColor: string | null;
  hasLogo: boolean;
  hasFavicon: boolean;
  portalSelfRegistration: boolean;
  microsoftLogin: boolean;
  brandingVersion: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UserItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  calendarAddress: string | null;
  status: UserStatus;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  createdAt: string;
  roles: { id: string; name: string; audience: Audience }[];
  mfaEnabled: boolean;
}

export interface RoleItem {
  id: string;
  key: string;
  name: string;
  description: string | null;
  audience: Audience;
  isSystem: boolean;
  permissions: string[];
  userCount: number;
  updatedAt: string;
}

export interface AuditItem {
  id: string;
  createdAt: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorUserId: string | null;
  actorLabel: string | null;
  ipAddress: string | null;
  data: unknown;
}

export interface TenantSettings {
  id: string;
  slug: string;
  name: string;
  legalName: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  vatId: string | null;
  timezone: string;
  primaryColor: string;
  accentColor: string | null;
  hasLogo: boolean;
  hasFavicon: boolean;
  portalSelfRegistration: boolean;
  repairSlipQr: boolean;
  repairPublicStatus: boolean;
  ticketGuestAccess: boolean;
  domains: { id: string; domain: string; isPrimary: boolean }[];
  numberSequences: { key: string; prefix: string; padding: number; nextValue: number }[];
}

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  isPlatform: boolean;
  status: 'ACTIVE' | 'SUSPENDED';
  createdAt: string;
  domains: { id: string; domain: string; isPrimary: boolean; createdAt: string }[];
  userCount: number;
}

export interface SessionItem {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

export interface DashboardData {
  layout: { key: string; size: 'sm' | 'md' | 'lg' }[];
  available: string[];
  customized: boolean;
  stats: Record<string, number>;
  myTickets: { id: string; number: string; subject: string; priority: Priority; status: string; customer: string | null; updatedAt: string }[] | null;
  readyRepairs: { id: string; number: string; device: string; customer: string; readyAt: string | null }[] | null;
  problems: { id: string; name: string; severity: number; host: string | null; customer: string | null; startedAt: string }[] | null;
  appointments: { id: string; title: string; startAt: string; endAt: string; location: string | null; ticket: string | null; repair: string | null }[] | null;
  logins7d: { day: string; count: number }[] | null;
  recentActivity: { id: string; createdAt: string; action: string; actorLabel: string | null }[] | null;
}

// --- Phase 2 ---------------------------------------------------------------
export type Priority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type StatusCategory = 'NEW' | 'OPEN' | 'WAITING' | 'RESOLVED' | 'CLOSED';
export type PortalAccess = 'PUBLIC' | 'REGISTERED' | 'CONTRACT' | 'INDIVIDUAL' | 'INTERNAL' | 'DISABLED';
export type Visibility = 'INTERNAL' | 'PUBLIC';

export interface Ref {
  id: string;
  name: string;
}

export interface StatusRef {
  id: string;
  name: string;
  color: string;
  category: StatusCategory;
}

export interface TicketListItem {
  id: string;
  number: string;
  subject: string;
  priority: Priority;
  source: string;
  status: StatusRef;
  area: { id: string; name: string; color: string };
  customer: Ref | null;
  assignee: Ref | null;
  requesterName: string | null;
  dueAt: string | null;
  followUpAt: string | null;
  lastActivityAt: string;
  createdAt: string;
  responseDueAt: string | null;
  resolutionDueAt: string | null;
  responseBreachedAt: string | null;
  resolutionBreachedAt: string | null;
  slaPausedAt: string | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
}

export interface TicketDetail extends TicketListItem {
  description: string;
  contract: { id: string; number: string; title: string } | null;
  slaPolicy: Ref | null;
  category: Ref | null;
  team: Ref | null;
  contact: { id: string; name: string; email: string | null; phone: string | null } | null;
  requesterEmail: string | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  comments: { id: string; visibility: Visibility; body: string; authorUserId: string | null; authorName: string | null; isCustomer: boolean; createdAt: string }[];
  events: { id: string; type: string; actorName: string | null; data: Record<string, unknown> | null; createdAt: string }[];
  attachments: { id: string; visibility: Visibility; commentId: string | null; createdAt: string; file: { originalName: string; mimeType: string; size: number } }[];
  timeEntries: { id: string; minutes: number; description: string | null; workDate: string; billable: boolean; user: Ref; rateName: string | null; rateCents: number | null }[];
  watchers: Ref[];
  billingWatching: boolean;
  billingQuery: { kind: 'ticket' | 'repair'; id: string; number: string } | null;
  links: { type: string; direction: 'out' | 'in'; ticket: { id: string; number: string; subject: string; status: StatusRef } }[];
  allowedStatusIds: string[];
  billingLock: LockState;
  repairs: { id: string; number: string; status: RepairStatus }[];
  devices: { id: string; manufacturer: string; deviceType: string; model: string; serialNumber: string | null; inventoryNumber: string | null; source: 'MANUAL' | 'NETDOC' | 'MONITORING' }[];
}

export interface TicketMeta {
  areas: { id: string; key: string; name: string; color: string; active: boolean }[];
  statuses: (StatusRef & { customerLabel: string; isDefault: boolean; sortOrder: number; scope: 'TICKET' | 'BILLING_QUERY' })[];
  categories: { id: string; name: string; areaId: string | null; parentId: string | null }[];
  teams: Ref[];
  agents: Ref[];
}

export interface CustomerItem {
  id: string;
  number: string;
  type: 'PRIVATE' | 'BUSINESS';
  salutation?: 'MR' | 'MRS' | 'FAMILY' | null;
  name: string;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  whatsappOptIn: boolean;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  vatId: string | null;
  notes: string | null;
  archivedAt: string | null;
  createdAt: string;
  openTickets: number;
}

export interface ContactItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  whatsappOptIn: boolean;
  position: string | null;
  isPrimary: boolean;
  notes: string | null;
  portal: { userId: string; status: UserStatus; roles: string[]; lastLoginAt: string | null } | null;
}

export interface CustomerErpInfo {
  erpNumber?: string | null;
  erpSyncedAt?: string | null;
  openErpChange?: { kind: string; createdAt: string; fields: string[] } | null;
}

export interface CustomerDetail extends CustomerItem, CustomerErpInfo {
  contacts: ContactItem[];
  areaGrantIds: string[];
  recentTickets: TicketListItem[];
  ticketCount: number;
}

export interface AreaConfig {
  id: string;
  key: string;
  name: string;
  description: string | null;
  color: string;
  portalAccess: PortalAccess;
  portalShowStatus: boolean;
  portalShowHistory: boolean;
  portalShowAssignee: boolean;
  portalShowDueDate: boolean;
  sortOrder: number;
  active: boolean;
  roleIds: string[];
  ticketCount: number;
}

export interface StatusConfig {
  id: string;
  name: string;
  customerLabel: string;
  category: StatusCategory;
  color: string;
  sortOrder: number;
  isDefault: boolean;
  active: boolean;
  nextStatusIds: string[];
  ticketCount: number;
}

export interface CategoryConfig {
  id: string;
  name: string;
  areaId: string | null;
  parentId: string | null;
  sortOrder: number;
  active: boolean;
}

export interface TeamConfig {
  id: string;
  name: string;
  description: string | null;
  members: Ref[];
}

export interface PortalMe {
  customer: { id: string; number: string; type: 'PRIVATE' | 'BUSINESS'; name: string; street: string | null; postalCode: string | null; city: string | null; phone: string | null; email: string | null };
  contact: { id: string; firstName: string; lastName: string; phone: string | null; mobile: string | null; email: string | null };
  areas: { id: string; name: string; description: string | null; color: string }[];
  companyWide: boolean;
  canManageUsers: boolean;
  canEditCustomer: boolean;
}

export interface PortalTicketItem {
  id: string;
  number: string;
  subject: string;
  area: { id: string; name: string; color: string };
  status: { label: string; category: StatusCategory } | null;
  isClosed: boolean;
  requesterName: string | null;
  createdAt: string;
  lastActivityAt: string;
}

export interface PortalTicketDetail extends PortalTicketItem {
  description: string;
  assigneeName: string | null;
  dueAt: string | null;
  messages: { id: string; body: string; authorName: string | null; fromCustomer: boolean; createdAt: string }[];
  history: { type: string; label: string; createdAt: string }[] | null;
  attachments: { id: string; name: string; mimeType: string; size: number; createdAt: string }[];
}

export interface CompanyUser {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: 'NONE' | UserStatus;
  isAdmin: boolean;
  isSelf: boolean;
}

// --- Phase 3 ---------------------------------------------------------------
export type RepairStatus =
  | 'RECEIVED'
  | 'DIAGNOSIS'
  | 'QUOTE_PENDING'
  | 'APPROVED'
  | 'IN_REPAIR'
  | 'WAITING_PARTS'
  | 'TESTING'
  | 'READY_FOR_PICKUP'
  | 'PICKED_UP'
  | 'CANCELLED';
export type RepairOutcome = 'REPAIRED' | 'NO_FAULT_FOUND' | 'NOT_REPAIRABLE' | 'RETURNED_UNREPAIRED';
export type WarrantyType = 'NONE' | 'WARRANTY' | 'STATUTORY' | 'GOODWILL';
export type PartStatus = 'NEEDED' | 'ORDERED' | 'RECEIVED' | 'INSTALLED';

export interface DeviceFields {
  manufacturer: string;
  deviceType: string;
  model: string;
  serialNumber: string | null;
  inventoryNumber: string | null;
  purchaseDate: string | null;
  warrantyUntil: string | null;
  notes: string | null;
}

export interface DeviceItem extends DeviceFields {
  id: string;
  customer: Ref | null;
  repairCount: number;
  createdAt: string;
}

export interface DeviceDetail extends DeviceItem {
  repairs: { id: string; number: string; status: RepairStatus; receivedAt: string; faultDescription: string; pickedUpAt: string | null }[];
}

export interface RepairListItem {
  id: string;
  number: string;
  status: RepairStatus;
  customer: Ref;
  device: { id: string; label: string; serialNumber: string | null };
  technician: Ref | null;
  faultDescription: string;
  receivedAt: string;
  estimatedCompletionAt: string | null;
  readyAt: string | null;
  updatedAt: string;
}

export interface QuoteItem {
  id?: string;
  kind: 'LABOR' | 'PART' | 'OTHER' | 'FLAT';
  description: string;
  quantity: number;
  /** Mengeneinheit, z. B. „Std.“, „Stk.“, „pausch.“ */
  unit: string;
  unitPriceCents: number;
  hourlyRateId?: string | null;
  flatRateId?: string | null;
}

export const QUOTE_KIND_LABELS: Record<QuoteItem['kind'], string> = { LABOR: 'Arbeitszeit', PART: 'Material', FLAT: 'Pauschale', OTHER: 'Sonstiges' };
export const QUOTE_UNITS = ['Std.', 'Min.', 'Stk.', 'pausch.', 'm', 'km', 'Satz'];
export const DEFAULT_QUOTE_UNIT: Record<QuoteItem['kind'], string> = { LABOR: 'Std.', PART: 'Stk.', OTHER: 'Stk.', FLAT: 'pausch.' };

/** Menge mit Einheit, z. B. „1,5 Std.“ */
export const formatQuantity = (q: number, unit?: string | null) => `${q.toLocaleString('de-DE', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`;

export interface CostStatus {
  actualNet: number;
  actualGross: number;
  reference: { kind: 'quote'; version: number; net: number; gross: number } | { kind: 'limit'; gross: number } | null;
  exceeded: boolean;
}

export interface LegalSummary {
  complete: boolean;
  items: { kind: 'terms' | 'privacy' | 'withdrawal'; title: string; url: string | null; hasContent: boolean; version: number; consentLabel: string }[];
}

export interface Quote {
  id: string;
  version: number;
  status: 'DRAFT' | 'SENT' | 'APPROVED' | 'REJECTED';
  taxRate: number;
  note: string | null;
  validUntil: string | null;
  sentAt: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionChannel: string | null;
  decisionNote: string | null;
  items: QuoteItem[];
  totals: { net: number; tax: number; gross: number };
  /** Online-Freigabe: bestätigte Rechtstexte */
  consent?: { acceptedAt: string; ip: string | null; documents: { kind: string; title: string; version: number }[] } | null;
}

export interface LockState {
  kind: 'billing' | 'closed' | null;
  canOverride: boolean;
  billingStatus: 'READY' | 'TRANSFERRED' | 'NOT_BILLABLE' | null;
  changeRequest: { requestedAt: string; requestedByName: string | null; reason: string } | null;
}

export interface RepairDetail extends RepairListItem {
  contact: { id: string; name: string; email: string | null; phone: string | null } | null;
  customerPhone: string | null;
  ticket: { id: string; number: string; subject: string } | null;
  outcome: RepairOutcome | null;
  condition: string | null;
  accessories: string[];
  warrantyType: WarrantyType;
  warrantyNote: string | null;
  costLimitCents: number | null;
  hasAccessCode: boolean;
  diagnosis: string | null;
  report: string | null;
  pickedUpAt: string | null;
  pickedUpBy: string | null;
  handoverNote: string | null;
  deviceInfo: DeviceFields & { id: string };
  steps: { id: string; kind: 'NOTE' | 'DIAGNOSIS' | 'WORK' | 'TEST'; text: string; minutes: number | null; rateName: string | null; rateCents: number | null; flatName: string | null; flatCents: number | null; testResult: 'PASSED' | 'FAILED' | null; customerVisible: boolean; authorId: string | null; authorName: string | null; createdAt: string }[];
  parts: { id: string; description: string; partNumber: string | null; supplier: string | null; quantity: number; unitPriceCents: number; status: PartStatus; orderedAt: string | null; receivedAt: string | null }[];
  quotes: Quote[];
  /** Tatsächlicher Aufwand im Vergleich zu KVA bzw. Kostenfreigabe */
  costs: CostStatus;
  attachments: { id: string; kind: string; customerVisible: boolean; createdAt: string; file: { originalName: string; mimeType: string; size: number } }[];
  events: { id: string; type: string; actorName: string | null; data: Record<string, unknown> | null; createdAt: string }[];
  totalMinutes: number;
  publicStatus: { url: string; pin: string } | null;
  readyPendingAt: string | null;
  lock: LockState;
  allowedTransitions: RepairStatus[];
  deviceHistory: { id: string; number: string; status: RepairStatus; receivedAt: string; faultDescription: string }[];
}

export interface PortalRepairItem {
  id: string;
  number: string;
  device: string;
  status: RepairStatus;
  statusLabel: string;
  receivedAt: string;
  readyAt: string | null;
  pickedUpAt: string | null;
  quotePending: boolean;
}

export interface PortalRepairDetail extends PortalRepairItem {
  serialNumber: string | null;
  faultDescription: string;
  accessories: string[];
  estimatedCompletionAt: string | null;
  outcome: string | null;
  report: string | null;
  steps: { text: string; testResult: string | null; createdAt: string }[];
  history: { label: string; createdAt: string }[];
  quotes: (Omit<Quote, 'id' | 'decidedBy' | 'decisionChannel' | 'decisionNote' | 'status'> & { status: 'SENT' | 'APPROVED' | 'REJECTED' })[];
  photos: { id: string; name: string; createdAt: string }[];
  documents: { type: string; title: string }[];
  canDecideQuote: boolean;
}

// --- Phase 4 ---------------------------------------------------------------
export interface FormItem {
  id: string;
  name: string;
  description: string | null;
  slug: string;
  kind: 'REQUEST' | 'CHECKLIST';
  access: 'PUBLIC' | 'PORTAL' | 'INTERNAL';
  active: boolean;
  areaId: string | null;
  routing: { fieldKey: string; map: Record<string, string> } | null;
  requireEmailConfirmation: boolean;
  publishedVersion: { id: string; version: number; publishedAt: string } | null;
  draftUpdatedAt: string;
  hasUnpublishedChanges: boolean;
  submissionCount: number;
  updatedAt: string;
}

export interface FormDetail extends FormItem {
  draft: unknown;
  versions: { id: string; version: number; publishedAt: string; publishedByName: string | null }[];
}

export interface FormSubmissionItem {
  id: string;
  createdAt: string;
  source: 'PUBLIC' | 'EMBED' | 'PORTAL' | 'INTERNAL';
  status: 'PENDING_CONFIRMATION' | 'PROCESSED';
  submitterName: string | null;
  submitterEmail: string | null;
  version: number;
  ticket: { id: string; number: string } | null;
  repair: { id: string; number: string } | null;
  formId: string;
  formName: string;
  data: Record<string, unknown>;
  definition: unknown;
  files: { id: string; name: string; mimeType: string; size: number }[];
}

// --- Phase 5: Microsoft 365 und Kalender -------------------------------------
export type ConnectionStatus = 'UNTESTED' | 'OK' | 'ERROR';

export interface M365Connection {
  id: string;
  directoryId: string;
  clientId: string;
  clientSecretExpiresAt: string | null;
  status: ConnectionStatus;
  lastError: string | null;
  lastCheckedAt: string | null;
  consentGrantedAt: string | null;
  calendarEnabled: boolean;
  loginEnabled: boolean;
  updatedAt: string;
}

export interface MailboxRule {
  match: 'senderDomain' | 'senderAddress' | 'subjectContains';
  value: string;
  areaId: string;
}

export interface MailboxItem {
  id: string;
  address: string;
  displayName: string | null;
  areaId: string | null;
  rules: MailboxRule[];
  syncEnabled: boolean;
  isDefaultSender: boolean;
  senderAreaIds: string[];
  senderForRepairs: boolean;
  acknowledge: boolean;
  markAsRead: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  createdAt: string;
}

export interface M365Overview {
  connection: M365Connection | null;
  mailboxes: MailboxItem[];
  setup: {
    consentRedirectUri: string;
    loginRedirectUri: string;
    requiredPermissions: { key: string; purpose: string }[];
    loginPermissions: string[];
  };
}

export interface TestResult {
  ok: boolean;
  message: string;
  grantedPermissions?: string[];
  missingPermissions?: string[];
}

export type EmailStatus = 'TICKET_CREATED' | 'ADDED_TO_TICKET' | 'IGNORED' | 'SENT' | 'FAILED';

export interface EmailItem {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  status: EmailStatus;
  mailbox: string | null;
  fromAddress: string;
  fromName: string | null;
  toAddresses: string[];
  subject: string;
  receivedAt: string;
  ticket: { id: string; number: string } | null;
  note: string | null;
}

export interface AppointmentItem {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  startAt: string;
  endAt: string;
  status: 'CONFIRMED' | 'CANCELLED';
  isOnlineMeeting: boolean;
  onlineMeetingUrl: string | null;
  technicians: { id: string; name: string }[];
  customerEmails: string[];
  ticket: { id: string; number: string; subject: string } | null;
  repair: { id: string; number: string } | null;
  customer: { id: string; name: string } | null;
  reminderMinutes: number | null;
  outlookSynced: boolean;
  syncError: string | null;
  createdAt: string;
}

export interface TechnicianItem {
  id: string;
  name: string;
  email: string;
  calendarAddress: string | null;
}

export interface BusySlot {
  start: string;
  end: string;
  source: 'portal' | 'outlook';
  label: string;
}

export interface AvailabilityItem {
  technicianId: string;
  name: string;
  outlookError: string | null;
  busy: BusySlot[];
}

export interface PortalAppointment {
  id: string;
  title: string;
  location: string | null;
  startAt: string;
  endAt: string;
  status: 'CONFIRMED' | 'CANCELLED';
  onlineMeetingUrl: string | null;
  technicians: string[];
  ticket: { id: string; number: string } | null;
  repair: { id: string; number: string } | null;
}

// --- Phase 6: Zabbix-Monitoring ----------------------------------------------
export type MonitoringSourceKind = 'ZABBIX' | 'NETDOC';

export interface ZabbixInstance {
  id: string;
  kind: MonitoringSourceKind;
  writeExternalIds: boolean;
  name: string;
  url: string;
  verifyTls: boolean;
  pollEnabled: boolean;
  active: boolean;
  status: ConnectionStatus;
  version: string | null;
  lastError: string | null;
  lastCheckedAt: string | null;
  lastHostSyncAt: string | null;
  lastPollAt: string | null;
  lastWebhookAt: string | null;
  hasWebhookSecret: boolean;
  autoTicket: boolean;
  minSeverity: number;
  areaId: string | null;
  teamId: string | null;
  assigneeId: string | null;
  requireCustomer: boolean;
  requireContract: boolean;
  resolveOnRecovery: boolean;
  allowAcknowledge: boolean;
  reportTickets: boolean;
  resolveInSource: boolean;
  hasPortalToken: boolean;
  portalTokenCreatedAt: string | null;
  portalTokenUsedAt: string | null;
  hostCount: number;
  openProblems: number;
}

export interface MonitoredSiteItem {
  id: string;
  instance: Ref & { kind: MonitoringSourceKind };
  externalId: string;
  name: string;
  code: string | null;
  address: string | null;
  sourceExternalId: string | null;
  group: { name: string; customer: Ref | null } | null;
  customer: Ref | null;
  contract: { id: string; number: string; title: string; status: string; endDate: string | null } | null;
  hostCount: number;
}

export interface WebhookSetup {
  kind: MonitoringSourceKind;
  url: string;
  script: string;
  parameters: { name: string; value: string }[];
}

export interface MonitoringEventItem {
  id: string;
  instance: Ref & { kind: MonitoringSourceKind };
  host: { id: string; name: string; host: string } | null;
  customer: Ref | null;
  zabbixEventId: string;
  name: string;
  severity: number;
  status: 'PROBLEM' | 'RESOLVED';
  startedAt: string;
  resolvedAt: string | null;
  acknowledged: boolean;
  ticket: { id: string; number: string } | null;
  ticketDecision: string | null;
  zabbixUrl: string;
  canAcknowledge: boolean;
  baseUid: string | null;
  occurrences: number;
  firstOccurredAt: string;
}

export interface MonitoringEventDetail extends MonitoringEventItem {
  history: { at: string; type: string; message: string; by: string }[];
}

export type AutoTicketMode = 'INHERIT' | 'ALWAYS' | 'NEVER';

export interface MonitoredHostItem {
  id: string;
  instance: Ref & { kind: MonitoringSourceKind };
  onlineStatus: string | null;
  externalUrl: string | null;
  lastSeenAt: string | null;
  zabbixHostId: string;
  host: string;
  name: string;
  enabled: boolean;
  removed: boolean;
  groups: string[];
  customer: Ref | null;
  groupCustomer: Ref | null;
  device: Ref | null;
  autoTicket: AutoTicketMode;
  openProblems: number;
  maxSeverity: number | null;
}

export interface MonitoredGroupItem {
  id: string;
  instance: Ref & { kind: MonitoringSourceKind };
  externalId: string | null;
  zabbixGroupId: string;
  name: string;
  customer: Ref | null;
  hostCount: number;
}

export interface MonitoringOverview {
  instances: { id: string; name: string; kind: MonitoringSourceKind; status: string; active: boolean; lastError: string | null; lastPollAt: string | null; lastWebhookAt: string | null }[];
  bySeverity: number[];
  openProblems: number;
  hosts: number;
  unassignedHosts: number;
}

// --- Phase 7: Verträge, SLA, Automatisierungen --------------------------------
export type ContractStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'TERMINATED';

export interface ContractItem {
  id: string;
  kind: 'SERVICE' | 'DPA';
  number: string;
  title: string;
  type: string;
  status: ContractStatus;
  effective: boolean;
  startDate: string;
  endDate: string | null;
  autoRenew: boolean;
  customer: Ref;
  slaPolicy: Ref | null;
  areas: Ref[];
  deviceCount: number;
  openTickets: number;
}

export interface ContractDetail extends ContractItem {
  renewalMonths: number;
  noticeDays: number | null;
  services: string[];
  notes: string | null;
  contact: { id: string; name: string; email: string | null } | null;
  owner: Ref | null;
  devices: { id: string; name: string; serialNumber: string | null }[];
  templateId: string | null;
  priceCents: number | null;
  billingInterval: BillingInterval | null;
  workBilling: 'BILLABLE' | 'INCLUDED' | 'QUOTA';
  quotaMinutes: number | null;
  quotaPeriod: 'MONTH' | 'QUARTER' | 'YEAR';
  quotaUsage: { label: string; usedMinutes: number; quotaMinutes: number } | null;
  clauses: ContractClause[];
  signedAt: string | null;
  signedBy: string | null;
  documents: ContractDocumentItem[];
  createdAt: string;
  updatedAt: string;
}

export type BillingInterval = 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | 'ONCE';
export interface ContractClause {
  heading: string;
  text: string;
}
export interface ContractDocumentItem {
  id: string;
  kind: 'GENERATED' | 'SIGNED';
  version: number;
  note: string | null;
  sentTo: string | null;
  sentAt: string | null;
  createdAt: string;
  file: { originalName: string; size: number; mimeType: string };
}
export interface ContractTemplateItem {
  id: string;
  kind: 'SERVICE' | 'DPA';
  name: string;
  type: string;
  title: string;
  description: string | null;
  services: string[];
  areaIds: string[];
  slaPolicyId: string | null;
  termMonths: number | null;
  autoRenew: boolean;
  renewalMonths: number;
  noticeDays: number | null;
  priceCents: number | null;
  billingInterval: BillingInterval | null;
  clauses: ContractClause[];
  active: boolean;
  contractCount: number;
}

export type PriorityMinutes = Partial<Record<Priority, number | null>>;

export interface SlaPolicyItem {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  allDay: boolean;
  businessHours: { day: number; from: string; to: string }[];
  holidays: string[];
  responseMinutes: PriorityMinutes;
  resolutionMinutes: PriorityMinutes;
  pauseWhenWaiting: boolean;
  contractCount: number;
}

export interface AutomationRuleItem {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  trigger: string;
  triggerParams: Record<string, number>;
  conditions: { field: string; op: string; value: unknown }[];
  actions: ({ type: string } & Record<string, unknown>)[];
  flow: unknown[] | null;
  sortOrder: number;
  runCount: number;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationRunItem {
  id: string;
  rule: Ref;
  trigger: string;
  entityType: string;
  entityId: string;
  status: 'SUCCESS' | 'FAILED';
  message: string;
  createdAt: string;
}
